import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  PurchasedLead,
  PendingPaidLead,
  fetchPurchasedLeads,
  fetchPendingPaidLeads,
  resumePaidLeads,
  updateLeadStage,
  unlockPaidLead,
} from '@/lib/myLeads';

/**
 * Loads every purchased lead for the signed-in contractor and, on the way,
 * finishes any confirmed-paid lead that was never unlocked (closed tab after a
 * hosted $29 checkout). Shared by the dashboard preview and /my-leads.
 */
export const useMyLeads = () => {
  const { profile } = useAuth();
  const [leads, setLeads] = useState<PurchasedLead[]>([]);
  const [pending, setPending] = useState<PendingPaidLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(0);

  const load = useCallback(
    async (autoResume = true) => {
      if (!profile?.id) return;
      setLoading(true);
      setError(false);
      try {
        let rows = await fetchPurchasedLeads(profile.id);
        let waiting = await fetchPendingPaidLeads(profile.id, rows.map((r) => r.listingId));
        if (autoResume && waiting.length) {
          const n = await resumePaidLeads(waiting);
          if (n > 0) {
            setJustUnlocked(n);
            rows = await fetchPurchasedLeads(profile.id);
            waiting = await fetchPendingPaidLeads(profile.id, rows.map((r) => r.listingId));
          }
        }
        setLeads(rows);
        setPending(waiting);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [profile?.id],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const setStage = useCallback(async (purchaseId: string, stage: string) => {
    setLeads((ls) => ls.map((l) => (l.id === purchaseId ? { ...l, stage } : l)));
    try {
      await updateLeadStage(purchaseId, stage);
    } catch {
      /* optimistic; next load restores the server value */
    }
  }, []);

  const unlockPending = useCallback(
    async (listingId: string) => {
      const r = await unlockPaidLead(listingId);
      await load(false);
      return r;
    },
    [load],
  );

  return { leads, pending, loading, error, justUnlocked, reload: () => load(false), setStage, unlockPending };
};
